<template>
    <div class="flex-fill flex-column no-scroll dashboard">
        <div class="flex-fill flex-column no-scroll py-4 px-6">
            <landing-page
                v-show="!activeDashboard"
                :dashboards="dashboards"
                @new="dashConfig = true"
                @select="activeDashboard = $event"
            />

            <dashboard
                v-if="activeDashboard"
                ref="dashboard"
                :key="refreshKey"
                :dashboard="activeDashboard"
                :getSavedSearchValues="getSavedSearchValues"
                :prepSavedSearchIds="prepSavedSearchIds"
                :getObjId="getObjId"
                @update="loadDashboards"
                @edit="editDash"
                @clone="cloneDash"
                @delete="deleteDash"
                @clear="activeDashboard = undefined"
                @reload="reloadActiveDashboard"
            />

            <dash-config
                v-if="dashConfig"
                :edit="activeDashboard"
                :clone="clone"
                :getSSValues="getSavedSearchValues"
                @close="closeConfig"
                @save="save"
                @saveClone="saveClone"
            />

            <fe-crud ref="dashboardsCrud" :config="dashboardsCrudConfig" autoload @read="dashboards = $event"/>
        </div>
    </div>
</template>

<script>
import IFrameRenderer from '@/components/common/IFrameRenderer'
import LandingPage from './LandingPage'
import Dashboard from "./Dashboard"
import DashConfig from "./config/DashConfig"
import {mapState} from "vuex"

export default {
    name: 'AchievementDashboard',

    components: {IFrameRenderer, LandingPage, Dashboard, DashConfig},

    props: ["selectedDashboardId"],

    data() {
        return {
            dashboards: [],
            refreshKey: 0,
            activeDashboard: undefined,
            clone: undefined,
            dashConfig: false
        }
    },

    computed: {
        ...mapState('global', ['sessionUser', 'userPreferences']),

        dashboardsCrudConfig() {
            let cfg = this.$_.cloneDeep(this.$models.dashboards)
            cfg.read.params.district_default = `0`
            return cfg
        }
    },

    mounted() {
        this.$store.dispatch('global/loadDemographics')
    },

    methods: {
        getSavedSearchValues(id, savedSearch = {}) {
            return new Promise(resolve => {
                if (!id) {
                    resolve({savedSearch})
                }

                let url = this.$models.getUrl('savedSearchValues', 'read') + '&id=' + id

                this.$axios.get(url)
                    .then(res => {
                        let ssValues = res.data.saved_search_values

                        Object.keys(ssValues).forEach(key => {
                            let value = ssValues[key]
                            if (value.length) {
                                let keyLower = key.toLowerCase()
                                let multi = [
                                    'school_id',
                                    'grade_id',
                                    'ethnicity',
                                    'disability_type_id',
                                    'tag_id',
                                    'incident_behavior_type_id',
                                    'incident_behavior_id',
                                    'incident_response_id',
                                    'target_descriptor_id',
                                    'demographic',
                                    'meal_status_id',
                                    'ell_level_id',
                                ]

                                if (multi.includes(keyLower)) {
                                    // Special handling for demographic values with comma-separated IDs
                                    if (keyLower === 'demographic') {
                                        // Process each demographic value
                                        savedSearch[keyLower] = value.flatMap(item => {
                                            if (item && item.id && item.id.includes(',') && item.id.indexOf(':') < item.id.indexOf(',')) {
                                                // Split "22:1,2" into ["22:1", "22:2"]
                                                const [prefix, values] = item.id.split(':')
                                                return values.split(',').map(val => ({
                                                    id: `${prefix}:${val}`,
                                                    name: `${prefix}:${val}`,
                                                    rcExclude: item.rcExclude || false
                                                }))
                                            }
                                            return [item]
                                        })
                                    } else {
                                        savedSearch[keyLower] = value
                                    }
                                } else if (keyLower === 'student_active_date') {
                                    savedSearch[keyLower] = value[0].id
                                } else {
                                    savedSearch[keyLower] = value[0]
                                }
                            }
                        })

                        resolve({savedSearch})
                    })
            })
        },

        async demoCheck(filter) {
            let optionIds = []
            let newFilters = []
            let ops = ['gt:', 'gte:', 'lt:', 'lte:', 'bt:', 'eq:', 'has:', 'start:', 'end:']

            filter.forEach(id => {
                const idValue = typeof id === 'object' ? id.id : id
                
                // If the filter already has an operator at the start, keep it as is
                if (ops.some(op => idValue.startsWith(op))) {
                    newFilters.push(idValue)
                    return
                }
                
                // For all other cases, extract the value part after the colon
                const parts = idValue.split(':')
                if (parts.length > 1) {
                    optionIds.push(parseInt(parts[1]))
                }
            })

            // Only fetch and process options if we have optionIds and no filters were kept as is
            if (optionIds.length > 0 && newFilters.length === 0) {
                await this.$axios.get(this.$models.getUrl('demographicOption', 'read'))
                    .then(r => {
                        let options = r.data.demographic_option
                        options.forEach(o => {
                            if (optionIds.indexOf(o.id) !== -1 && o.display_name_group) {
                                o.display_name_group.forEach(n => {
                                    newFilters.push(o.demographic_id + ':' + n)
                                })
                            } else if (optionIds.indexOf(o.id) !== -1) {
                                newFilters.push(o.demographic_id + ':' + o.id)
                            }
                        })
                    })
            }

            return newFilters
        },

        displayNameCheck(filter) {
            // Handle case when filter is a single object, not an array
            if (!Array.isArray(filter)) {
                filter = [filter]
            }
            
            if (!filter || !filter.length) return filter
            
            let studentFilters = []
            let newFilters = []

            filter.forEach(f => {
                if (f.display_name_group) {
                    let dng = f.display_name_group
                    dng.forEach((g,j) => {
                        studentFilters[j] = {
                            ...f,
                            id: g,
                            name: g,
                            value: g,
                        }
                        newFilters.push(studentFilters[j])
                    })
                } else {
                    newFilters.push(f)
                }
            })

            return newFilters
        },

        async prepSavedSearchIds(config, operation) {
            let output = {}

            // if v has display_name_group, split group and create new objects
            // as if user sent multiple options
            // do this for: ethnicity, gender, meal_status_id, disability_type_id, ell_level_id
            if (config.ethnicity) config.ethnicity = this.displayNameCheck(config.ethnicity)
            if (config.gender) config.gender = this.displayNameCheck(config.gender)
            if (config.meal_status_id) config.meal_status_id = this.displayNameCheck(config.meal_status_id)
            if (config.disability_type_id) config.disability_type_id = this.displayNameCheck(config.disability_type_id)
            if (config.ell_level_id) config.ell_level_id = this.displayNameCheck(config.ell_level_id)

            // Set empty string for empty demographic arrays
            if (config.demographic?.length === 0) {
                output.demographic = ''
            } 
            else if (config?.demographic?.length) {
                // Extract the ID from each demographic filter
                const demographicValues = config.demographic.map(
                    filter => typeof filter === 'string' ? filter : filter.id
                )
                
                // Join into comma-separated string for the API
                if (demographicValues.length > 0) {
                    output.demographic = demographicValues.join(',')
                }
            }

            // Handle student_active_flag from config if it exists
            if (config.student_active_flag && config.student_active_flag.id !== null) {
                output.student_active_flag = Number(config.student_active_flag.id)
            }

            // Process other filters
            this.$_.forEach(config, (v, k) => {
                if (v && k !== 'demographic' && k !== 'student_active_flag') {
                    if (!this.$_.isArray(v)) v = [v]
                    output[k] = v.map(x => this.getObjId(x)).join(',')
                }
            })

            if (operation === 'create') {
                output.kpi_force = 1
            }

            return [output]
        },

        getObjId(v) {
            if (!v) return

            // If v is already a string in the format "id:eq:value" or "eq:value", return it as is
            if (typeof v === 'string' && (v.includes(':') || v.startsWith('eq:') || v.startsWith('gt:') || v.startsWith('gte:') || v.startsWith('lt:') || v.startsWith('lte:') || v.startsWith('bt:') || v.startsWith('has:') || v.startsWith('start:') || v.startsWith('end:'))) {
                return v
            }

            // for ethnicity and gender
            // add id with value of display_name or value
            if (!v.hasOwnProperty('id')) Object.assign(v, {id: v.value})

            let id = v.hasOwnProperty('id') ? v.id : v
            if (v.rcExclude) id = '!' + id
            if (typeof id === 'string') {
                id = id?.replaceAll(/,/g, "comma;")
            }

            return id
        },

        loadDashboards() {
            return this.$refs.dashboardsCrud.read()
        },

        editDash() {
            this.clone = undefined
            
            // If the active dashboard has a saved search ID, load the current filter values
            if (this.activeDashboard && this.activeDashboard.saved_search_id) {
                // Clone the current activeDashboard to avoid reference issues
                const dashboardToEdit = {...this.activeDashboard}
                
                // Load the current filter values before showing the edit form
                this.getSavedSearchValues(dashboardToEdit.saved_search_id)
                    .then(({savedSearch}) => {
                        // Attach the filter data directly to the dashboard object
                        dashboardToEdit.demographic = savedSearch.demographic
                        
                        // Make sure demographic filters are properly formatted
                        if (savedSearch.demographic && savedSearch.demographic.length > 0) {
                            // Ensure each demographic filter has the right format
                            dashboardToEdit.demographic = savedSearch.demographic.map(filter => {
                                if (typeof filter === 'string') {
                                    // If it's a string, convert to object format
                                    return {
                                        id: filter,
                                        name: filter,
                                        rcExclude: false
                                    }
                                }
                                return filter
                            })                            
                        }
                        
                        this.activeDashboard = dashboardToEdit
                        this.dashConfig = true
                    })
            } else {
                this.dashConfig = true
            }
        },

        cloneDash(config) {
            this.clone = {
                description: config.description,
                school_year_id: config.school_year_id,
                global_filters: config.global_filters,
                saved_search_id: config.saved_search_id,
                has_vis_filters: !!config.configs.filter(x => !!x.saved_search_id),
                scope: config.scope
            }
            this.dashConfig = true
        },

        deleteDash(config) {
            let msg = 'You are attempting to delete ' + config.name + '.'
            msg += '<br>This action cannot be undone.'
            msg += '<br>Are you sure you want to delete this dashboard?'

            this.$confirmDelete(
                config,
                () => {
                    this.$refs.dashboardsCrud.destroy(config)
                        .then(() => {
                            this.loadDashboards()
                            this.activeDashboard = undefined
                        })
                },
                null,
                msg
            )
        },

        async saveClone(config, savedSearch = {}) {
            let url = this.$models.getUrl('dashboards', 'clone')
            let awaiting = []
            config.id = this.activeDashboard.id

            if (config.config.global_filters) {
                var args = await this.prepSavedSearchIds(savedSearch, 'create')
                awaiting.push(this.postSavedSearch(null, 'create', args))
            } else if (config.config.saved_search_id) {
                config.config.saved_search_id = null
            }

            if (awaiting.length) {
                const responses = await Promise.all(awaiting)
                if (responses[0]?.data?.saved_searches?.length) {
                    config.config.saved_search_id = responses[0].data.saved_searches[0].id
                }
            }

            const response = await this.$axios.post(url, [config])

            if (response?.data?.data[0]?.id) {
                const newDashId = response?.data?.data[0]?.id
                const dashboards = await this.loadDashboards()
                let newDash = dashboards.data.kpi_dashboard.find(x => x.id === newDashId)
                let innerAwaiting = []
                let configsWithSS = newDash.configs.filter(x => !['section', 'googlesheet'].includes(x.config.type))

                if (newDash.saved_search_id && configsWithSS.length) {
                    innerAwaiting.push(this.updateVisSavedSearch(configsWithSS, args))
                }
                Promise.all(innerAwaiting).then(() => {
                    this.dashConfig = false
                    this.refreshKey += 1
                    this.activeDashboard = newDash
                })
            }
        },

        async save(config, savedSearch = {}) {
            let operation = config.id ? 'update' : 'create'
            let awaiting = []

            if (config.global_filters) {
                let ssOperation = config.saved_search_id ? 'update' : 'create'
                // Use the savedSearch object that's passed in, which should contain the current filter state
                var args = await this.prepSavedSearchIds(savedSearch, ssOperation)
                awaiting.push(this.postSavedSearch(config.saved_search_id, ssOperation, args))
            } else if (config.saved_search_id) {
                config.saved_search_id = null
            }

            // If we have no pending operations, just update the dashboard
            if (!awaiting.length) {
                this.$refs.dashboardsCrud[operation](config)
                    .then(res => {
                        this.loadDashboards().then(() => {
                            this.activeDashboard = res.data.kpi_dashboard[0]
                            this.dashConfig = false
                        })
                    })
                return
            }

            Promise.all(awaiting)
                .then(res => {
                    if (res[0]?.data?.saved_searches?.length) {
                        config.saved_search_id = res[0].data.saved_searches[0].id
                    }

                    let innerAwaiting = []
                    let reload = false

                    if (operation !== 'create') {
                        let configsWithSS = config.configs.filter(x => !['section', 'googlesheet'].includes(x.config.type))
                        if (config.saved_search_id && configsWithSS.length) {
                            reload = true
                            innerAwaiting.push(this.updateVisSavedSearch(configsWithSS, args))
                        }
                    }

                    Promise.all(innerAwaiting)
                        .then(() => {
                            this.$refs.dashboardsCrud[operation](config)
                                .then(res => {
                                    this.loadDashboards().then(() => {
                                        this.activeDashboard = res.data.kpi_dashboard[0]
                                        if (reload) {
                                            this.refreshKey += 1
                                            this.$nextTick(() => {
                                                this.$refs.dashboard.reloadVisualizations()
                                            })
                                        }
                                        this.dashConfig = false
                                    })
                                })
                        })
                })
        },

        updateVisSavedSearch(configs, global) {
            let awaiting = []

            configs.forEach(x => {
                this.getSavedSearchValues(x.saved_search_id)
                    .then(async res => {
                        let operation = x.saved_search_id ? 'update' : 'create'
                        let existing = await this.prepSavedSearchIds(res.savedSearch, operation)
                        let globalFilters = this.$_.cloneDeep(global[0])
                        if (x.config.type === 'tag' && globalFilters.hasOwnProperty('tag_id')) {
                            delete globalFilters.tag_id
                        }
                        let newSS = Object.assign({}, existing[0], globalFilters)

                        awaiting.push(this.postSavedSearch(x.saved_search_id, operation, [newSS])
                            .then(res => {
                                if (res?.data?.saved_searches?.[0]?.id && x.saved_search_id !== res.data.saved_searches[0].id) {
                                    x.saved_search_id = res.data.saved_searches[0].id
                                    awaiting.push(this.updateConfig(x))
                                }
                            }))
                    })
            })

            return new Promise(resolve => {
                if (!awaiting.length) {
                    resolve(true)
                    return
                }
                Promise.all(awaiting).then(() => resolve(true))
            })
        },

        postSavedSearch(id, operation, args) {
            let property = id ? '&property=dashboard&saved_search_id=' + id : ''
            let ssUrl = this.$models.getUrl('savedSearch', operation) + property
            return this.$axios.post(ssUrl, {'saved_searches': args})
        },

        updateConfig(data) {
            let config = this.$_.cloneDeep(data)
            let cfg = this.$_.cloneDeep(config.config)
            if (config.id) delete config.i
            Object.keys(cfg).forEach(x => {
                if (config.hasOwnProperty(x)) {
                    if (x === 'colors' && this.$_.isArray(config[x])) {
                        let colors = {}
                        config[x].forEach(y => {
                            if (y.color) {
                                colors[y.name] = y.color
                            }
                        })
                        cfg[x] = colors
                    } else {
                        cfg[x] = config[x]
                    }
                    delete config[x]
                }
            })
            config.config = JSON.stringify(cfg)
            let url = this.$models.getUrl('visualizations', 'update')
            return this.$axios.post(url, {'kpi_config': config})
        },

        reloadActiveDashboard() {
            let active = this.activeDashboard
            this.$refs.dashboardsCrud.read()
                .then(response => {
                    this.activeDashboard = response.data.kpi_dashboard.find(x => x.id == active.id)
                })
        },

        closeConfig() {
            this.dashConfig = false
            this.clone = undefined
        }
    }
}
</script>

<style lang="scss" scoped>
.dashboard {
    background: #F5F6F7;
}
</style>
